<script setup>
import { defineAsyncComponent } from 'vue'
import LogoItem from './controls/LogoItem.vue'
import { usePortAllData } from '@/stores/portAllData'
import { storeToRefs } from 'pinia'
import { ref, inject, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { authorizedControl, authorizedModule } from '@/plugins/utils/Authorization.js'
import GenericTooltip from './common/GenericTooltip.vue';
import { useI18n } from 'vue-i18n';

const ZoomControl = defineAsyncComponent(() => import('./controls/ZoomControl.vue'))
const FullScreenControl = defineAsyncComponent(() => import('./controls/FullScreenControl.vue'))
const OverviewControl = defineAsyncComponent(() => import('./controls/OverviewControl.vue'))
import MeasureControl from './controls/measure/MeasureControl.vue'
import PolylineMeasureControl from './controls/polylineMeasure/PolylineMeasureControl.vue'
const SpeedVectorControl = defineAsyncComponent(() => import('./controls/SpeedVectorControl.vue'))
const AisAnchorLabel = defineAsyncComponent(() => import('./controls/AisAnchorLabel.vue'))
const CarViz = defineAsyncComponent(() => import('./controls/CarViz.vue'))
const GpsAutoCenter = defineAsyncComponent(() => import('./controls/GpsAutoCenter.vue'))
const CenterPosition = defineAsyncComponent(() => import('./controls/centerPosition/CenterPosition.vue'))
const CenterPositionImage = defineAsyncComponent(() => import('./controls/centerPosition/CenterPositionImage.vue'))
const CleanAis = defineAsyncComponent(() => import('./controls/CleanAis.vue'))
const QuotationControl = defineAsyncComponent(() => import('./controls/QuotationControl.vue'))
const EditMesureUnit = defineAsyncComponent(() => import('./common/EditMeasureUnit.vue'))
const EtaControl = defineAsyncComponent(() => import('./controls/EtaControl.vue'))
import ManOverBoard from './controls/ManOverBoard.js'
import Notifications from './controls/Notifications.js'

// import { DELETE_TILE_CACHE_EVENT } from '@/plugins/utils/events.js'

const { t } = useI18n();

const $map = inject('$map')
const $config = inject('$config')
const controls = $config.controls
const store = usePortAllData()
const { getAuthConfig, getIsMobile, showDraughtControl, getModuleOpen } = storeToRefs(store)
const { setDisplayDraughtControl } = store

const isAuthorized = (type, key) => {
  if (type === 'control') return authorizedControl(key, getAuthConfig.value.authorizedControls)
  else if (type === 'module') return authorizedModule(key, getAuthConfig.value.authorizedModules)
  return false
}

const mob = ref(null)
const notif = ref(null)

const measureControl = ref(null)
const polylineMeasureControl = ref(null)
const unitToggleIsActive = ref(false)
const measureIsActive = ref(false)
const polylineIsActive = ref(false)

onMounted(() => {
  if (isAuthorized('control', 'manOverBoard')) {
    mob.value = new ManOverBoard($map)
    mob.value.start()
  }
  if (isAuthorized('module', 'notification')) {
    notif.value = new Notifications()
    notif.value.start()
  }
})

onBeforeUnmount(() => {
  if (isAuthorized('control', 'manOverBoard') && mob.value) {
    mob.value.stop()
  }
  if (isAuthorized('module', 'notification') && notif.value) {
    notif.value.stop()
  }
})

const displayControl = (key) => {
  const control = controls[key]
  if (key === 'draughtTideSlider') return control.enabled && getIsMobile.value && isAuthorized('control', 'draughtTideSlider') && control.mobile
  else return control.enabled && (control.right ? isAuthorized('control', key) : true) && (getIsMobile.value ? control.mobile : true)
}

const toggleDraught = () => {
  if (showDraughtControl.value) setDisplayDraughtControl(false)
  else setDisplayDraughtControl(true)
}

const showControlBar = computed(() => {
  if (getIsMobile.value && getModuleOpen.value) return false
  return true
})

watch(getModuleOpen, (nv) => {
  if (nv && showDraughtControl.value) setDisplayDraughtControl(false)
})

const isActiveMeasure = (isActive) => {
  if (isActive && polylineMeasureControl.value && polylineIsActive.value) {
    polylineMeasureControl.value.toggleMeasure()
    polylineIsActive.value = false
  }
  measureIsActive.value = isActive
  unitToggleIsActive.value = isActive  
}

const isActivePolylineMeasure = (isActive) => {
  if (isActive && measureControl.value && measureIsActive.value) {
    measureControl.value.toggleMeasure()
    measureIsActive.value = false
  }
  polylineIsActive.value = isActive
  unitToggleIsActive.value = isActive  
}

// const refreshLayers = () => {
//   document.dispatchEvent(new CustomEvent(DELETE_TILE_CACHE_EVENT))
// }

</script>

<template>
  <div class="geomod-control-bar">
    <div><logo-item /></div>
    <div v-show="showControlBar" class="control-bar">
      <generic-tooltip v-if="displayControl('fullScreen')" :text="t('fullScreen')" position="bottom">
        <full-screen-control />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('overview') && Array.isArray(controls.overview.layers)" :text="t('overview')" position="bottom">
        <overview-control />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('measure')" :text="t('measure')" position="bottom">
        <measure-control ref="measureControl" :options="controls.measure" @is-active="isActiveMeasure" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('polylineMeasure')" :text="t('polylineMeasure')" position="bottom">
        <polyline-measure-control ref="polylineMeasureControl" :options="controls.polylineMeasure" @is-active="isActivePolylineMeasure" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('speedVector')" :text="t('speedVector')" position="bottom">
        <speed-vector-control :options="controls.speedVector" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('aisAnchorLabel')" :text="t('aisAnchorLabel')" position="bottom">
        <ais-anchor-label :options="controls.aisAnchorLabel" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('carViz')" :text="t('carViz')" position="bottom">
        <car-viz :options="controls.carViz" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('gpsCenter')" :text="t('gpsCenter')" position="bottom">
        <gps-auto-center :options="controls.gpsCenter" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('centerPosition')" :text="t('centerPosition')" position="bottom">
        <center-position :options="controls.centerPosition" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('cleanAIS')" :text="t('cleanAIS')" position="bottom">
        <clean-ais :options="controls.cleanAIS" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('displayQuotations')" :text="t('displayQuotations')" position="bottom">
        <quotation-control :options="controls.displayQuotations" />
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('draughtTideSlider')" :text="t('draughtTideSlider')" position="bottom">
        <button :class="{ active: showDraughtControl }" @click="toggleDraught">
          <i class="icon-depth" />
        </button>
      </generic-tooltip>
      <generic-tooltip v-if="displayControl('eta')" :text="t('etaControl')" position="bottom">
        <eta-control :options="controls.eta" />
      </generic-tooltip>
      <edit-mesure-unit v-if="unitToggleIsActive" />
      <!-- <button @click="refreshLayers"></button> -->
    </div>
  </div>
  <zoom-control v-if="displayControl('zoom')" :zoom-slider="displayControl('zoomSlider')" />
  <center-position-image v-if="displayControl('centerPosition')" />
</template>

<i18n src="@/locales/modulesControls.json"></i18n>